/* @jsx h */
import { defineComponent, h } from "vue";
import {
  APPLE_ROW_META,
  APPLE_ROWS,
  type ApplePassStyle,
  type WalletPassDesign,
  type WalletPassDesignDocument,
} from "../../types";
import {
  ColorField,
  FieldKeyPicker,
  FieldKeySelect,
  PanelRow,
  PanelSection,
  TemplateInput,
  ToggleRow,
} from "./primitives";

/**
 * Where each content field appears on one platform. Fields are defined once in
 * the Content tab; these panels place them by key and never repeat a binding.
 */

interface PlatformPanelProps {
  design: WalletPassDesign;
  onChange: (patch: Partial<WalletPassDesignDocument>) => void;
}

const STYLES: { value: ApplePassStyle; label: string }[] = [
  { value: "storeCard", label: "Store card" },
  { value: "generic", label: "Generic" },
  { value: "coupon", label: "Coupon" },
  { value: "eventTicket", label: "Event ticket" },
];

export const ApplePanel = defineComponent<PlatformPanelProps>({
  name: "ApplePanel",
  props: ["design", "onChange"],
  setup(props) {
    const apple = () => props.design.document.apple;
    const patch = (p: Partial<WalletPassDesignDocument["apple"]>) =>
      props.onChange({ apple: { ...apple(), ...p } });
    const fields = () => props.design.document.content.fields;
    const posterAllowed = () =>
      apple().style === "storeCard" || apple().style === "generic";

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection title="Apple Wallet">
          <ToggleRow
            label="Issue an Apple Wallet pass"
            hint="Off: no .pkpass is generated, emailed or offered, and installed passes stop updating."
            value={apple().enabled}
            onChange={(enabled) => patch({ enabled })}
          />
          <PanelRow label="Pass style">
            <select
              class="select select-bordered select-sm"
              value={apple().style}
              onChange={(e) => {
                const style = (e.target as HTMLSelectElement)
                  .value as ApplePassStyle;
                patch({
                  style,
                  ...(style === "storeCard" || style === "generic"
                    ? {}
                    : { poster: { ...apple().poster, enabled: false } }),
                });
              }}
            >
              {STYLES.map((style) => (
                <option key={style.value} value={style.value}>
                  {style.label}
                </option>
              ))}
            </select>
          </PanelRow>
        </PanelSection>

        <PanelSection
          title="Field rows"
          description="Apple silently truncates, so each row is capped at what renders."
        >
          {APPLE_ROWS.map((row) => (
            <PanelRow
              key={row}
              label={`${APPLE_ROW_META[row].label} (max ${APPLE_ROW_META[row].max})`}
              hint={APPLE_ROW_META[row].hint}
            >
              <FieldKeyPicker
                fields={fields()}
                value={apple().rows[row]}
                max={APPLE_ROW_META[row].max}
                onChange={(keys) =>
                  patch({ rows: { ...apple().rows, [row]: keys } })
                }
              />
            </PanelRow>
          ))}
        </PanelSection>

        <PanelSection
          title="iOS 27 poster"
          description="Full-bleed artwork with the primary and secondary rows over it. Older iOS shows the rows above."
        >
          <ToggleRow
            label="Add the poster layout"
            hint={
              posterAllowed()
                ? "Uses the design's artwork."
                : "Only store cards and generic passes support the poster."
            }
            value={apple().poster.enabled}
            disabled={!posterAllowed()}
            onChange={(enabled) =>
              patch({ poster: { ...apple().poster, enabled } })
            }
          />
          {apple().poster.enabled && (
            <>
              <ColorField
                label="Poster text"
                value={apple().poster.text_color}
                onChange={(text_color) =>
                  patch({ poster: { ...apple().poster, text_color } })
                }
              />
              <ColorField
                label="Poster secondary text"
                value={apple().poster.secondary_text_color}
                onChange={(secondary_text_color) =>
                  patch({ poster: { ...apple().poster, secondary_text_color } })
                }
              />
              <PanelRow
                label={`Scrim opacity — ${Math.round(apple().poster.scrim_opacity * 100)}%`}
              >
                <input
                  type="range"
                  min="0"
                  max="100"
                  class="range range-sm range-primary"
                  value={apple().poster.scrim_opacity * 100}
                  onInput={(e) =>
                    patch({
                      poster: {
                        ...apple().poster,
                        scrim_opacity:
                          Number((e.target as HTMLInputElement).value) / 100,
                      },
                    })
                  }
                />
              </PanelRow>
            </>
          )}
        </PanelSection>

        <PanelSection title="Behaviour">
          <PanelRow
            label="Relevant date"
            hint="When Wallet surfaces the pass on the lock screen."
          >
            <TemplateInput
              kind={props.design.kind}
              value={apple().relevance.date}
              onChange={(date) =>
                patch({ relevance: { ...apple().relevance, date } })
              }
            />
          </PanelRow>
          <ToggleRow
            label="Use restaurant location"
            hint="Surfaces the pass when the customer is near the venue."
            value={apple().relevance.use_restaurant_location}
            onChange={(use_restaurant_location) =>
              patch({
                relevance: { ...apple().relevance, use_restaurant_location },
              })
            }
          />
          <ToggleRow
            label="Prevent sharing"
            value={apple().sharing_prohibited}
            onChange={(sharing_prohibited) => patch({ sharing_prohibited })}
          />
          <ToggleRow
            label="Suppress strip shine"
            hint="Removes Apple's gloss over the strip image."
            value={apple().suppress_strip_shine}
            onChange={(suppress_strip_shine) => patch({ suppress_strip_shine })}
          />
          <PanelRow
            label="Grouping identifier"
            hint="Passes sharing it stack together in Wallet."
          >
            <input
              class="input input-bordered input-sm font-mono"
              value={apple().grouping_identifier}
              onInput={(e) =>
                patch({
                  grouping_identifier: (e.target as HTMLInputElement).value,
                })
              }
            />
          </PanelRow>
        </PanelSection>
      </div>
    );
  },
});

export const GooglePanel = defineComponent<PlatformPanelProps>({
  name: "GooglePanel",
  props: ["design", "onChange"],
  setup(props) {
    const google = () => props.design.document.google;
    const patch = (p: Partial<WalletPassDesignDocument["google"]>) =>
      props.onChange({ google: { ...google(), ...p } });
    const fields = () => props.design.document.content.fields;

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection title="Google Wallet">
          <ToggleRow
            label="Issue a Google Wallet pass"
            hint="Off: no object is created or offered, and saved passes become inactive."
            value={google().enabled}
            onChange={(enabled) => patch({ enabled })}
          />
          <PanelRow
            label="Header"
            hint="Required by Google; falls back to the program name when empty."
          >
            <FieldKeySelect
              fields={fields()}
              value={google().header}
              onChange={(header) => header && patch({ header })}
            />
          </PanelRow>
          <PanelRow label="Subheader">
            <FieldKeySelect
              fields={fields()}
              value={google().subheader}
              allowNone={true}
              onChange={(subheader) => patch({ subheader })}
            />
          </PanelRow>
        </PanelSection>

        <PanelSection
          title="Card rows"
          description="Up to three rows on the front of the card, each with one to three fields."
        >
          {google().rows.map((row, index) => (
            <PanelRow key={index} label={`Row ${index + 1}`}>
              <FieldKeyPicker
                fields={fields()}
                value={row}
                max={3}
                onChange={(keys) => {
                  const rows = google().rows.map((r, i) =>
                    i === index ? keys : r,
                  );
                  patch({ rows: rows.filter((r) => r.length > 0) });
                }}
              />
            </PanelRow>
          ))}
          <button
            class="btn btn-sm btn-outline w-full"
            disabled={google().rows.length >= 3 || fields().length === 0}
            onClick={() =>
              patch({ rows: [...google().rows, [fields()[0].key]] })
            }
          >
            <span class="material-symbols-outlined text-sm">add</span>
            Add row
          </button>
        </PanelSection>

        <PanelSection
          title="Details"
          description="Text modules below the card, shown when the pass is opened."
        >
          <FieldKeyPicker
            fields={fields()}
            value={google().modules}
            max={10}
            onChange={(modules) => patch({ modules })}
          />
        </PanelSection>
      </div>
    );
  },
});

export const BrowserPanel = defineComponent<PlatformPanelProps>({
  name: "BrowserPanel",
  props: ["design", "onChange"],
  setup(props) {
    const browser = () => props.design.document.browser;
    const patch = (p: Partial<WalletPassDesignDocument["browser"]>) =>
      props.onChange({ browser: { ...browser(), ...p } });
    const fields = () => props.design.document.content.fields;

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection
          title="Collected card"
          description="The card customers see in the Lemmon customer app."
        >
          <ToggleRow
            label="Show in Collected"
            hint="Off: the card is hidden from the customer app. Apple and Google are unaffected."
            value={browser().enabled}
            onChange={(enabled) => patch({ enabled })}
          />
          <PanelRow label="Top-right field" hint="Shown large, e.g. a balance.">
            <FieldKeySelect
              fields={fields()}
              value={browser().primary}
              allowNone={true}
              onChange={(primary) => patch({ primary })}
            />
          </PanelRow>
          <PanelRow label="Panel fields (max 3)">
            <FieldKeyPicker
              fields={fields()}
              value={browser().secondary}
              max={3}
              onChange={(secondary) => patch({ secondary })}
            />
          </PanelRow>
          <ToggleRow
            label="Show reward progress"
            hint="A progress bar toward the next reward (coin programs)."
            value={browser().show_progress}
            onChange={(show_progress) => patch({ show_progress })}
          />
        </PanelSection>
      </div>
    );
  },
});
